import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";
import SearchBox from "./SearchBox";

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const linkBase =
    "relative px-1 py-2 text-sm font-medium tracking-wide transition-colors whitespace-nowrap";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1320px] items-center gap-6 px-4 md:h-20 md:px-6">
        <Link to="/" className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-md font-heading text-lg font-bold transition-colors ${
              transparent
                ? "bg-background-50/15 text-background-50 ring-1 ring-background-50/40"
                : "bg-primary-500 text-background-50"
            }`}
          >
            由
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              {siteInfo.brand}
            </span>
            <span
              className={`mt-1 text-[10px] tracking-[0.32em] ${
                transparent ? "text-background-100/80" : "text-foreground-500"
              }`}
            >
              {siteInfo.brandEn}
            </span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-6 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `${linkBase} ${
                  isActive
                    ? transparent
                      ? "text-background-50"
                      : "text-primary-600"
                    : transparent
                      ? "text-background-100/90 hover:text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.name}
                  <span
                    className={`absolute -bottom-0.5 left-1/2 h-0.5 -translate-x-1/2 rounded-full transition-all ${
                      isActive ? "w-4 opacity-100" : "w-0 opacity-0"
                    } ${transparent ? "bg-background-50" : "bg-primary-500"}`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-64 items-center md:flex lg:w-72">
          <SearchBox variant="navbar" className="w-full" />
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          className={`ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md transition-colors md:hidden ${
            transparent
              ? "text-background-50 hover:bg-background-50/15"
              : "text-foreground-800 hover:bg-background-100"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="px-4 py-4">
            <SearchBox variant="inline" className="mb-4 w-full" />
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) =>
                    `flex cursor-pointer items-center justify-between border-b border-background-200 py-3 text-sm font-medium ${
                      isActive ? "text-primary-600" : "text-foreground-800"
                    }`
                  }
                >
                  {link.name}
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
}